import { useState, useCallback, useMemo } from "react";
import {
  Box,
  Grid,
  TextField,
  Typography,
  Button,
  Autocomplete,
  Chip,
  Alert,
  CircularProgress,
} from "@material-ui/core";
import { Edit as EditIcon } from "@material-ui/icons";
import PropTypes from "prop-types";
import { propuestasApi } from "src/services/propuestasApi";
import { sucursalesApi } from "src/services/sucursalesApi";
import { clientesApi } from "src/services/clientesApi";
import { useSnackbar } from "src/components/snackbar";
import EditarSolicitanteDialog from "src/components/propuesta/EditarSolicitanteDialog";
import CampoTextoIA from "../components/CampoTextoIA";
import { BRAND } from "src/theme/brandTokens";
import { normalizarRut } from "src/utils/propuestasV2/normalizarRut";

const FORM_NUEVO_INICIAL = { nombre: "", rut: "", correo: "", telefono: "" };

// Normalizacion del correo para el dedup solicitante <-> cliente. El rut usa el
// normalizador compartido, que replica el criterio del backend.
const normMail = (m) => (m || "").trim().toLowerCase();

/**
 * Paso 1 — Portada
 * Campos: Solicitante (Autocomplete lazy), Sucursal, Título, Descripción.
 */
const StepPortada = ({ formik, camposIA, desmarcarCampoIA }) => {
  const { enqueueSnackbar } = useSnackbar();

  // Solicitante
  const [inputSolicitante, setInputSolicitante] = useState("");
  const [solicitanteSeleccionado, setSolicitanteSeleccionado] = useState(null);
  const [triggerBuscar, { data: solicitantesResultados = [], isFetching }] =
    propuestasApi.useLazyBuscarSolicitantesQuery();

  const handleInputSolicitante = useCallback(
    (_, value) => {
      setInputSolicitante(value);
      if (value.trim().length >= 2) {
        triggerBuscar({ q: value.trim() });
      }
    },
    [triggerBuscar],
  );

  // Clientes (activos + inactivos) para poder usarlos como solicitante.
  // Reusa la misma query cacheada que el selector de cliente de las fases.
  const { data: clientes = [] } = clientesApi.useGetClientesQuery({
    estado: "TODOS",
  });

  // Fusion de las dos fuentes del selector: solicitantes (busqueda server-side)
  // + clientes (lista cacheada, filtrada en el front por el texto tecleado).
  // Dedup por rut/correo: si la persona ya existe como solicitante, no se
  // repite su gemelo cliente.
  const opcionesCombinadas = useMemo(() => {
    const solicitantes = solicitantesResultados.map((s) => ({
      id: s.id,
      nombre: s.nombre,
      rut: s.rut || null,
      correo: s.correo || null,
      telefono: s.telefono || null,
      _origen: "SOLICITANTE",
    }));

    const q = inputSolicitante.trim().toLowerCase();
    if (q.length < 2) return solicitantes;

    const clavesSolicitante = new Set();
    solicitantes.forEach((s) => {
      if (s.rut) clavesSolicitante.add(`rut:${normalizarRut(s.rut)}`);
      if (s.correo) clavesSolicitante.add(`mail:${normMail(s.correo)}`);
    });

    const clientesFiltrados = clientes
      .filter((c) => {
        const coincideTexto = [c.nombre, c.rut, c.correo]
          .filter(Boolean)
          .some((v) => v.toLowerCase().includes(q));
        if (!coincideTexto) return false;
        if (c.rut && clavesSolicitante.has(`rut:${normalizarRut(c.rut)}`)) {
          return false;
        }
        if (c.correo && clavesSolicitante.has(`mail:${normMail(c.correo)}`)) {
          return false;
        }
        return true;
      })
      .map((c) => ({
        id: null,
        nombre: c.nombre,
        rut: c.rut || null,
        correo: c.correo || null,
        telefono: c.telefono || null,
        _origen: "CLIENTE",
      }));

    return [...solicitantes, ...clientesFiltrados];
  }, [solicitantesResultados, clientes, inputSolicitante]);

  // El value del Autocomplete debe estar entre las options. Un solicitante
  // elegido via "Usar este solicitante" viene de una busqueda puntual y puede
  // no estar en la lista del texto actual: se antepone para que MUI lo matchee.
  const opcionesConSeleccion = useMemo(() => {
    if (!solicitanteSeleccionado?.id) return opcionesCombinadas;
    const yaEsta = opcionesCombinadas.some(
      (o) => o.id != null && o.id === solicitanteSeleccionado.id,
    );
    return yaEsta
      ? opcionesCombinadas
      : [
          { ...solicitanteSeleccionado, _origen: "SOLICITANTE" },
          ...opcionesCombinadas,
        ];
  }, [opcionesCombinadas, solicitanteSeleccionado]);

  const handleSeleccionarSolicitante = (_, option) => {
    // Cualquier nueva seleccion descarta una captura de correo pendiente,
    // para no dejar el panel abierto apuntando a un cliente ya reemplazado.
    setClientePendienteCorreo(null);
    setCorreoClienteNuevo("");
    if (option?._origen === "CLIENTE") {
      seleccionarClienteComoSolicitante(option);
      return;
    }
    setSolicitanteSeleccionado(option);
    formik.setFieldValue("solicitanteId", option?.id ?? null);
    formik.setFieldValue("solicitanteNuevo", null);
    // Guardar datos completos para que el preview pueda mostrar nombre/correo
    formik.setFieldValue("solicitanteData", option ?? null);
  };

  // Un cliente elegido como solicitante se materializa como "solicitante nuevo"
  // (el backend hace find-or-create al guardar). Usa el correo PERSONAL del
  // cliente (cliente.correo), no el de facturacion.
  const seleccionarClienteComoSolicitante = (cliente) => {
    const correo = (cliente.correo || "").trim();
    if (!correo) {
      setClientePendienteCorreo(cliente);
      setCorreoClienteNuevo("");
      return;
    }
    const nuevo = {
      nombre: cliente.nombre,
      rut: cliente.rut || undefined,
      correo,
      telefono: cliente.telefono || undefined,
    };
    formik.setFieldValue("solicitanteNuevo", nuevo);
    formik.setFieldValue("solicitanteId", null);
    formik.setFieldValue("solicitanteData", { ...nuevo, id: null });
    setSolicitanteSeleccionado(null);
    setInputSolicitante("");
  };

  const confirmarCorreoCliente = async () => {
    const correo = correoClienteNuevo.trim();
    if (!correo) {
      enqueueSnackbar("Ingresa el correo personal del cliente.", {
        variant: "warning",
      });
      return;
    }
    try {
      await editarCliente({
        rut: clientePendienteCorreo.rut,
        datos: { correo },
      }).unwrap();
      // El cliente ya tiene correo; reusar el mapeo normal.
      seleccionarClienteComoSolicitante({
        ...clientePendienteCorreo,
        correo,
      });
      setClientePendienteCorreo(null);
      setCorreoClienteNuevo("");
      enqueueSnackbar("Correo del cliente guardado.", { variant: "success" });
    } catch {
      enqueueSnackbar("No se pudo guardar el correo del cliente.", {
        variant: "error",
      });
    }
  };

  // Solicitante nuevo
  const [mostrarFormNuevo, setMostrarFormNuevo] = useState(false);
  const [formNuevo, setFormNuevo] = useState(FORM_NUEVO_INICIAL);
  const [alertaRut, setAlertaRut] = useState(null);
  // Solicitante encontrado como duplicado, pendiente de que el usuario decida usarlo
  const [duplicadoEncontrado, setDuplicadoEncontrado] = useState(null);

  // Cliente elegido como solicitante que aún no tiene correo personal: se pide
  // inline antes de fijarlo como solicitante.
  const [clientePendienteCorreo, setClientePendienteCorreo] = useState(null);
  const [correoClienteNuevo, setCorreoClienteNuevo] = useState("");
  const [editarCliente, { isLoading: isGuardandoCorreo }] =
    clientesApi.useEditarClienteMutation();

  // Diálogo de edición de solicitante existente
  const [openEditarDialog, setOpenEditarDialog] = useState(false);

  const handleCampoNuevo = (campo) => (e) => {
    setFormNuevo((prev) => ({ ...prev, [campo]: e.target.value }));
    if (campo === "rut") setAlertaRut(null);
    // Limpiar el duplicado al volver a editar los campos
    setDuplicadoEncontrado(null);
  };

  const limpiarFormNuevo = () => {
    setFormNuevo(FORM_NUEVO_INICIAL);
    setAlertaRut(null);
    setDuplicadoEncontrado(null);
  };

  const validarYConfirmarNuevo = async () => {
    if (!formNuevo.nombre.trim()) {
      enqueueSnackbar("El nombre del solicitante es requerido.", {
        variant: "warning",
      });
      return;
    }
    if (!formNuevo.correo.trim()) {
      enqueueSnackbar("El correo del solicitante es requerido.", {
        variant: "warning",
      });
      return;
    }

    // Verificar duplicado por RUT
    if (formNuevo.rut.trim()) {
      const resultado = await triggerBuscar({ q: formNuevo.rut.trim() })
        .unwrap()
        .catch(() => []);
      const duplicadoRut = resultado.find(
        (s) =>
          s.rut && s.rut.toLowerCase() === formNuevo.rut.trim().toLowerCase(),
      );
      if (duplicadoRut) {
        setAlertaRut("error");
        setDuplicadoEncontrado(duplicadoRut);
        return;
      }
    }

    // Verificar duplicado por correo (advertencia blanda, no bloqueante: el
    // correo no es un identificador confiable, puede ser una persona distinta
    // con un correo generico compartido).
    const resultadoCorreo = await triggerBuscar({ q: formNuevo.correo.trim() })
      .unwrap()
      .catch(() => []);
    const duplicadoCorreo = resultadoCorreo.find(
      (s) =>
        s.correo &&
        s.correo.toLowerCase() === formNuevo.correo.trim().toLowerCase(),
    );
    if (duplicadoCorreo) {
      enqueueSnackbar(
        `Ya existe un solicitante con ese correo (${duplicadoCorreo.nombre}). Verifica que no sea la misma persona.`,
        { variant: "warning" },
      );
    }

    // Sin duplicados: confirmar como solicitante nuevo
    formik.setFieldValue("solicitanteNuevo", {
      nombre: formNuevo.nombre.trim(),
      rut: formNuevo.rut.trim() || undefined,
      correo: formNuevo.correo.trim(),
      telefono: formNuevo.telefono.trim() || undefined,
    });
    formik.setFieldValue("solicitanteId", null);
    setSolicitanteSeleccionado(null);
    setMostrarFormNuevo(false);
    limpiarFormNuevo();
  };

  const handleUsarDuplicado = () => {
    if (!duplicadoEncontrado) return;
    handleSeleccionarSolicitante(null, duplicadoEncontrado);
    setMostrarFormNuevo(false);
    limpiarFormNuevo();
  };

  const cancelarFormNuevo = () => {
    limpiarFormNuevo();
    setMostrarFormNuevo(false);
  };

  const solicitanteActual = formik.values.solicitanteId
    ? solicitanteSeleccionado
    : formik.values.solicitanteNuevo
      ? { ...formik.values.solicitanteNuevo, id: null }
      : null;

  // Sucursal
  const { data: sucursales = [] } = sucursalesApi.useGetSucursalesQuery();

  // Aviso cuando el formulario viene de migrar una V1 TAREA sin solicitante:
  // el vendor debe seleccionar o crear uno antes de avanzar al siguiente paso.
  const necesitaSolicitanteMigracion =
    formik.values.origenMigracion?.tipo === "TAREA" &&
    formik.values.origenMigracion?.solicitanteId == null &&
    !formik.values.solicitanteId &&
    !formik.values.solicitanteNuevo;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Portada
      </Typography>

      {necesitaSolicitanteMigracion && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Esta propuesta proviene de una migración desde V1 (tipo TAREA) que no
          tenía solicitante asignado. Selecciona un solicitante existente o crea
          uno nuevo antes de continuar. Sugerencia: busca por el nombre del
          cliente del primer ítem.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Solicitante */}
        <Grid item xs={12} id="seccion-solicitante">
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Solicitante
          </Typography>

          {!mostrarFormNuevo && (
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12} sm={8}>
                <Autocomplete
                  options={opcionesConSeleccion}
                  getOptionLabel={(o) =>
                    `${o.nombre}${o.rut ? ` — ${o.rut}` : ""}${o.correo ? ` (${o.correo})` : ""}`
                  }
                  isOptionEqualToValue={(option, value) =>
                    option.id != null && option.id === value.id
                  }
                  inputValue={inputSolicitante}
                  onInputChange={handleInputSolicitante}
                  value={solicitanteSeleccionado}
                  onChange={handleSeleccionarSolicitante}
                  loading={isFetching}
                  noOptionsText={
                    inputSolicitante.length < 2
                      ? "Escribe al menos 2 caracteres"
                      : "Sin resultados"
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Buscar solicitante existente"
                      placeholder="Nombre, correo o RUT..."
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isFetching && <CircularProgress size={16} />}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li
                      {...props}
                      key={`${option._origen}-${option.id ?? ""}-${option.rut ?? ""}-${option.correo ?? ""}-${option.nombre}`}
                    >
                      <Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            flexWrap: "wrap",
                          }}
                        >
                          <Typography variant="body2" component="span">
                            {option.nombre}
                          </Typography>
                          <Chip
                            label={
                              option._origen === "CLIENTE"
                                ? "Cliente"
                                : "Solicitante"
                            }
                            size="small"
                            sx={{ height: 18, fontSize: 10 }}
                          />
                          {!option.rut && (
                            <Chip
                              label="Sin RUT"
                              size="small"
                              sx={{
                                bgcolor: BRAND.color.primary,
                                color: "#fff",
                                height: 18,
                                fontSize: 10,
                              }}
                            />
                          )}
                        </Box>
                        <Typography variant="caption" color="textSecondary">
                          {[option.rut, option.correo]
                            .filter(Boolean)
                            .join(" · ")}
                        </Typography>
                      </Box>
                    </li>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => {
                    setMostrarFormNuevo(true);
                    handleSeleccionarSolicitante(null, null);
                  }}
                  sx={{ height: 56 }}
                >
                  Crear nuevo solicitante
                </Button>
              </Grid>
            </Grid>
          )}

          {clientePendienteCorreo && (
            <Box
              sx={{
                mt: 1,
                p: 2,
                border: "1px solid",
                borderColor: "warning.light",
                borderRadius: 1,
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {clientePendienteCorreo.nombre} no tiene correo personal
                configurado
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Ingrésalo para usar este cliente como solicitante. Se guardará
                en la ficha del cliente.
              </Typography>
              <Grid container spacing={2} sx={{ mt: 0.5 }} alignItems="center">
                <Grid item xs={12} sm={7}>
                  <TextField
                    fullWidth
                    autoFocus
                    label="Correo personal *"
                    type="email"
                    value={correoClienteNuevo}
                    onChange={(e) => setCorreoClienteNuevo(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      variant="contained"
                      onClick={confirmarCorreoCliente}
                      disabled={isGuardandoCorreo}
                      sx={{
                        bgcolor: BRAND.color.primary,
                        "&:hover": { bgcolor: BRAND.color.primaryDark },
                      }}
                    >
                      Guardar y usar
                    </Button>
                    <Button
                      variant="text"
                      color="inherit"
                      onClick={() => {
                        setClientePendienteCorreo(null);
                        setCorreoClienteNuevo("");
                      }}
                    >
                      Cancelar
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}

          {mostrarFormNuevo && (
            <Box
              sx={{
                mt: 1,
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Nuevo solicitante
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Nombre *"
                    value={formNuevo.nombre}
                    onChange={handleCampoNuevo("nombre")}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="RUT (opcional)"
                    value={formNuevo.rut}
                    onChange={handleCampoNuevo("rut")}
                    error={alertaRut === "error"}
                    helperText={
                      alertaRut === "error"
                        ? "Ya existe un solicitante con ese RUT."
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Correo *"
                    type="email"
                    value={formNuevo.correo}
                    onChange={handleCampoNuevo("correo")}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Teléfono (opcional)"
                    value={formNuevo.telefono}
                    onChange={handleCampoNuevo("telefono")}
                  />
                </Grid>
              </Grid>
              {/* Bloqueo por RUT duplicado */}
              {alertaRut === "error" && duplicadoEncontrado && (
                <Alert
                  severity="error"
                  sx={{ mt: 2 }}
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={handleUsarDuplicado}
                    >
                      Usar este solicitante
                    </Button>
                  }
                >
                  Ya existe un solicitante con ese RUT:{" "}
                  <strong>{duplicadoEncontrado.nombre}</strong>
                  {duplicadoEncontrado.correo
                    ? ` (${duplicadoEncontrado.correo})`
                    : ""}
                  .
                </Alert>
              )}

              <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                <Button
                  variant="contained"
                  onClick={validarYConfirmarNuevo}
                  sx={{
                    bgcolor: BRAND.color.primary,
                    "&:hover": { bgcolor: BRAND.color.primaryDark },
                  }}
                >
                  Confirmar
                </Button>
                <Button
                  variant="text"
                  color="inherit"
                  onClick={cancelarFormNuevo}
                >
                  Cancelar
                </Button>
              </Box>
            </Box>
          )}

          {solicitanteActual && !mostrarFormNuevo && (
            <Box
              sx={{
                mt: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Chip
                label={
                  solicitanteActual.id
                    ? `${solicitanteActual.nombre} (existente)`
                    : `${solicitanteActual.nombre} (nuevo)`
                }
                onDelete={() => {
                  formik.setFieldValue("solicitanteId", null);
                  formik.setFieldValue("solicitanteNuevo", null);
                  formik.setFieldValue("solicitanteData", null);
                  setSolicitanteSeleccionado(null);
                  setInputSolicitante("");
                }}
              />
              {!solicitanteActual.rut && (
                <Chip
                  label="Sin RUT"
                  size="small"
                  sx={{ bgcolor: BRAND.color.primary, color: "#fff" }}
                />
              )}
              {/* Solo se puede editar si el solicitante ya existe en la BD */}
              {solicitanteActual.id && (
                <Button
                  size="small"
                  startIcon={<EditIcon fontSize="small" />}
                  onClick={() => setOpenEditarDialog(true)}
                  sx={{ textTransform: "none" }}
                >
                  Editar
                </Button>
              )}
            </Box>
          )}

          <EditarSolicitanteDialog
            open={openEditarDialog}
            onClose={() => setOpenEditarDialog(false)}
            solicitante={solicitanteActual?.id ? solicitanteActual : null}
            onSaved={(actualizado) => {
              setSolicitanteSeleccionado(actualizado);
              formik.setFieldValue("solicitanteData", actualizado);
            }}
          />
        </Grid>

        {/* Sucursal */}
        <Grid item xs={12} sm={6}>
          <Autocomplete
            options={sucursales}
            getOptionLabel={(o) =>
              typeof o === "string" ? o : o.label || o.nombre
            }
            value={
              sucursales.find((s) => s.nombre === formik.values.sucursal) ||
              null
            }
            onChange={(_, suc) => {
              formik.setFieldValue("sucursal", suc?.nombre ?? "");
              formik.setFieldValue("vendedor", "");
            }}
            renderInput={(params) => (
              <TextField {...params} label="Sucursal *" />
            )}
          />
        </Grid>

        <Grid item xs={12} id="seccion-titulo">
          <CampoTextoIA
            formik={formik}
            name="titulo"
            label="Título del proyecto"
            contextIA="Título de una propuesta comercial tributaria. Debe ser breve y descriptivo."
            camposIA={camposIA}
            desmarcarCampoIA={desmarcarCampoIA}
          />
        </Grid>

        <Grid item xs={12} id="seccion-descripcion">
          <CampoTextoIA
            formik={formik}
            name="descripcion"
            label="Descripción del proyecto"
            multiline
            minRows={3}
            contextIA="Descripción general del proyecto en una propuesta comercial en el contexto tributario chileno."
            camposIA={camposIA}
            desmarcarCampoIA={desmarcarCampoIA}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

StepPortada.propTypes = {
  formik: PropTypes.object.isRequired,
  camposIA: PropTypes.instanceOf(Set),
  desmarcarCampoIA: PropTypes.func,
};

StepPortada.defaultProps = {
  camposIA: new Set(),
  desmarcarCampoIA: undefined,
};

export default StepPortada;
